"use client";

import { MapPin } from "lucide-react";

/**
 * A map link **inside a card that is already a link**.
 *
 * Every result card is one big `<Link>` to the business, so a real `<a>` for
 * the location would be an anchor inside an anchor: invalid markup, and
 * browsers resolve it by dropping one of them, so the map link would silently
 * stop working. This is a `role="link"` span that stops the click before it
 * reaches the card, which is the same trick the booking screen's in-clinic card
 * already uses.
 *
 * Keyboard users get the same thing: it is focusable and answers to Enter and
 * Space, and the click never bubbles up to open the business instead.
 *
 * Where the surrounding element is *not* a link, use `MapLink` and get a real
 * anchor.
 */
export function CardMapLink({
  href,
  children,
  className,
}: {
  href: string;
  /** The place to show. Omit for a bare "Location". */
  children?: React.ReactNode;
  className?: string;
}) {
  const open = () => window.open(href, "_blank", "noopener");

  return (
    <span
      role="link"
      tabIndex={0}
      aria-label={typeof children === "string" ? children : "Location"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        open();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        e.stopPropagation();
        open();
      }}
      className={`inline-flex cursor-pointer items-center gap-1 font-medium text-accent hover:underline ${className ?? ""}`}
    >
      <MapPin className="size-3.5 shrink-0" aria-hidden strokeWidth={1.8} />
      <span className="truncate">{children ?? "Location"}</span>
    </span>
  );
}
